/**
 * UranIA PA — API client admin tenants (US-028).
 *
 * Wrapper su `fetch` per i path admin del backend `services/api-gateway`:
 *   - `GET  /api/v1/tenants`                  -> list (platform_admin only)
 *   - `GET  /api/v1/tenants/{tenantId}`       -> detail
 *   - `POST /api/v1/tenants/{tenantId}:disable` -> disable (step-up MFA)
 *   - `POST /api/v1/tenants/{tenantId}:enable`  -> re-enable (step-up MFA)
 *
 * AC US-028 #4 letterale: l'azione "disable" richiede MFA step-up;
 * lato client significa che il server può rispondere `403` con
 * `WWW-Authenticate: step-up` + Problem+JSON `acr_required=[...]`.
 * `TenantsApiError` espone `acrRequired` cosi' che la UI possa offrire
 * il re-login con l'acr atteso (riallineato a US-013 step-up).
 *
 * Boundary:
 *   - Bearer JWT preso da `useAuthStore.accessToken` (in-memory, FR-F1-10).
 *   - `credentials: 'include'` per SSO cookie Keycloak (silent renew).
 *   - Problem+JSON RFC 9457 quando 4xx; fallback su `statusText` se il
 *     server non emette Problem valido.
 *   - Niente PII nei log: la pagina logga `subject` + `tenant_id` ma non
 *     attributi free-text.
 *
 * NB: il backend espone `POST /api/v1/tenants` da US-008 ma list/disable
 * sono planned (US-F1-05 "tenant onboarding API + UI" copre l'intera
 * superficie). Il client mantiene il contratto stabile e fallisce
 * graceful (Problem+JSON) finche' gli endpoint non saranno completi.
 */

import { loadSpaEnv } from '../env.ts';
import { useAuthStore } from '../auth/auth-store.ts';

export type TenantStatus = 'active' | 'disabled';

export type TenantDataClassification = 'public' | 'internal' | 'restricted';

export interface TenantSummaryDto {
  readonly tenant_id: string;
  readonly nome: string;
  readonly codice_adg: string;
  readonly regione: string;
  readonly status: TenantStatus;
  readonly data_classification_default: TenantDataClassification;
  readonly created_at: string;
  readonly disabled_at?: string | null;
}

export interface TenantDetailDto extends TenantSummaryDto {
  readonly dpo_email: string;
  readonly qdrant_collections: readonly [string, string];
  readonly keycloak_group: string;
}

export interface TenantListDto {
  readonly items: readonly TenantSummaryDto[];
  readonly total: number;
}

export interface TenantsApiErrorPayload {
  readonly status: number;
  readonly code: string | null;
  readonly title: string;
  readonly detail: string | null;
  readonly acrRequired?: readonly string[] | null;
}

export class TenantsApiError extends Error {
  public readonly status: number;
  public readonly code: string | null;
  public readonly detail: string | null;
  public readonly acrRequired: readonly string[] | null;

  constructor(payload: TenantsApiErrorPayload) {
    super(payload.title);
    this.name = 'TenantsApiError';
    this.status = payload.status;
    this.code = payload.code;
    this.detail = payload.detail;
    this.acrRequired = payload.acrRequired ?? null;
  }
}

function authHeader(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  if (token === null || token.length === 0) {
    return {};
  }
  return { Authorization: `Bearer ${token}` };
}

function buildUrl(path: string): string {
  const env = loadSpaEnv();
  const base = env.apiBaseUrl.replace(/\/+$/u, '');
  return `${base}${path}`;
}

interface ProblemBody {
  readonly title?: unknown;
  readonly detail?: unknown;
  readonly status?: unknown;
  readonly code?: unknown;
  readonly acr_required?: unknown;
}

function readStringArray(value: unknown): readonly string[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  return value.filter((v): v is string => typeof v === 'string');
}

async function parseProblem(response: Response): Promise<TenantsApiError> {
  let body: ProblemBody = {};
  try {
    body = (await response.json()) as ProblemBody;
  } catch {
    // server non ha emesso Problem+JSON: fallback su statusText.
  }
  const title =
    typeof body.title === 'string' && body.title.length > 0
      ? body.title
      : response.statusText || 'http_error';
  const detail = typeof body.detail === 'string' ? body.detail : null;
  const code = typeof body.code === 'string' ? body.code : null;
  return new TenantsApiError({
    status: response.status,
    code,
    title,
    detail,
    acrRequired: readStringArray(body.acr_required),
  });
}

async function requestJson(
  method: 'GET' | 'POST' | 'PUT',
  path: string,
  body?: unknown,
): Promise<unknown> {
  const init: RequestInit = {
    method,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...authHeader(),
    },
    credentials: 'include',
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  let response: Response;
  try {
    response = await fetch(buildUrl(path), init);
  } catch (err) {
    throw new TenantsApiError({
      status: 0,
      code: 'network_error',
      title: err instanceof Error ? err.message : 'network_error',
      detail: null,
    });
  }
  if (!response.ok) {
    throw await parseProblem(response);
  }
  if (response.status === 204) {
    return null;
  }
  return (await response.json()) as unknown;
}

export async function listTenants(): Promise<TenantListDto> {
  const raw = (await requestJson('GET', '/api/v1/tenants')) as TenantListDto;
  return raw;
}

export async function getTenant(tenantId: string): Promise<TenantDetailDto> {
  const raw = (await requestJson(
    'GET',
    `/api/v1/tenants/${encodeURIComponent(tenantId)}`,
  )) as TenantDetailDto;
  return raw;
}

export interface DisableTenantBody {
  readonly motivo: string;
}

export async function disableTenant(
  tenantId: string,
  body: DisableTenantBody,
): Promise<TenantDetailDto> {
  const raw = (await requestJson(
    'POST',
    `/api/v1/tenants/${encodeURIComponent(tenantId)}:disable`,
    body,
  )) as TenantDetailDto;
  return raw;
}

export async function enableTenant(tenantId: string): Promise<TenantDetailDto> {
  const raw = (await requestJson(
    'POST',
    `/api/v1/tenants/${encodeURIComponent(tenantId)}:enable`,
  )) as TenantDetailDto;
  return raw;
}

export interface CreateTenantBody {
  readonly nome: string;
  readonly codice_adg: string;
  readonly regione: string;
  readonly dpo_email: string;
  readonly data_classification_default: TenantDataClassification;
}

export interface CreateTenantResponseDto {
  readonly tenant_id: string;
  readonly codice_adg: string;
  readonly qdrant_collections: readonly [string, string];
  readonly keycloak_group: string;
  readonly outbox_event_id: string;
  readonly audit_event_id: string;
}

export async function createTenant(
  body: CreateTenantBody,
): Promise<CreateTenantResponseDto> {
  return (await requestJson('POST', '/api/v1/tenants', body)) as CreateTenantResponseDto;
}

export interface UpdateTenantBody {
  readonly nome: string;
  readonly regione: string;
  readonly dpo_email: string;
  readonly data_classification_default: TenantDataClassification;
}

export async function updateTenant(
  tenantId: string,
  body: UpdateTenantBody,
): Promise<TenantDetailDto> {
  return (await requestJson(
    'PUT',
    `/api/v1/tenants/${encodeURIComponent(tenantId)}`,
    body,
  )) as TenantDetailDto;
}
